import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoRounded from '@mui/icons-material/InfoRounded';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { getAgents } from '../agentsConfig';
import type { OpenXAgentSession } from '../types';

type OpenXAgentHistoryProps = {
  sessions: OpenXAgentSession[];
};

export const OpenXAgentHistory = ({ sessions }: OpenXAgentHistoryProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 'fontWeightMedium',
            lineHeight: '18px',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {t('History')}
        </Typography>
        <Tooltip
          title={t('Sessions automatically expire 30 days after creation.')}
          arrow
          slotProps={{ popper: { sx: { zIndex: 10001 } } }}
          placement="right"
        >
          <IconButton aria-label={t('history info')} size="small" sx={{ p: 0, color: 'text.secondary' }}>
            <InfoRounded sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {getAgents(t).map(({ id, name }) => {
          const agentSessions = sessions.filter(s => s.agentId === id);

          return (
            <Accordion
              key={id}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                overflow: 'hidden',
                '&::before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  px: 3,
                  py: 2,
                  minHeight: 'unset',
                  '&.Mui-expanded': { minHeight: 'unset' },
                  '& .MuiAccordionSummary-content': { my: 0 },
                  '& .MuiAccordionSummary-content.Mui-expanded': { my: 0 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 'fontWeightBold',
                    lineHeight: '20px',
                    color: 'text.primary',
                  }}
                >
                  {name}
                </Typography>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  px: 0,
                  py: 0,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {agentSessions.length === 0 ? (
                  <Typography
                    sx={{ fontSize: '12px', fontWeight: 'fontWeightRegular', color: 'text.secondary', px: 3, py: 1.5 }}
                  >
                    {t('No {name} sessions so far.', { name })}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      maxHeight: 220,
                      overflow: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      py: 1.5,
                    }}
                  >
                    {agentSessions.map(({ id: sessionId, timestamp, keyword, timeAgo, path }) => (
                      <Box
                        key={sessionId}
                        sx={{
                          height: 36,
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          px: 3,
                          py: 0.5,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 'fontWeightBold',
                              lineHeight: '18px',
                              color: 'text.primary',
                              mb: '-4px',
                            }}
                          >
                            {timestamp}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '10px',
                              fontWeight: 'fontWeightRegular',
                              lineHeight: '14px',
                              color: 'text.secondary',
                            }}
                          >
                            {keyword}
                            {path ? ` · ${path}` : ''} | {timeAgo}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
};
